import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import i18n, { t } from 'i18next';
import type { CSSProperties } from 'react';

import { COLORS } from 'Components/foundation';
import UButton from 'Components/unit/UButton/UButton';
import UIcon from 'Components/unit/UIcon/UIcon';
import UIllustration from 'Components/unit/UIllustration/UIllustration';
import UTextLink from 'Components/unit/UTextLink/UTextLink';

import { AI_MODELS } from 'Pages/Courses/Edition/views/AIInitial/AIInitial';
import SIAPrompt from '../SIAPrompt/SIAPrompt';
import styles, { IA_ICON_SIZE, ILLUSTRATION_SIZE } from './SIAPromptContainer.style';

const IA_HELP_URL = 'https://support.sparted.com/hc/en-us/articles/21001047533596--Générer-un-parcours-avec-l-IA';

export type SIAPromptContainerProps = {
  initialPrompt?: string;
  generationCount?: number;
  onClickActionButtonHandler: (prompt: string) => Promise<unknown>;
  style?: CSSProperties;
};

export const SIAPromptContainer: React.FC<SIAPromptContainerProps> = ({
  initialPrompt,
  generationCount,
  onClickActionButtonHandler,
  style = {},
}) => {
  const displayGenerationCounter = generationCount === undefined ? false : true;

  const [isGenerationButtonActive, setIsGenerationButtonActive] = useState<boolean>(false);
  const [promptText, setPromptText] = useState<string | undefined>(initialPrompt);
  const [showdevMenu, setShowDevMenu] = useState<boolean>(false);

  const strings = useMemo(
    () => ({
      actionGenerate: t('structural_component:s_ia_prompt_container.actionGenerate'),
      generationCountdown: t('structural_component:s_ia_prompt_container.generationCountdown', {
        count: generationCount,
      }),
      tagNew: t('structural_component:s_ia_prompt_container.tagNew'),
      tagBeta: t('structural_component:s_ia_prompt_container.tagBeta'),
      title: t('structural_component:s_ia_prompt_container.title'),
    }),
    [t, generationCount, i18n.isInitialized],
  );

  useEffect(() => {
    if (!promptText) return setIsGenerationButtonActive(false);
    if (!isGenerationButtonActive && promptText?.length > 0) {
      setIsGenerationButtonActive(true);
    }
    if (isGenerationButtonActive && promptText?.length === 0) {
      setIsGenerationButtonActive(false);
    }
  }, [isGenerationButtonActive, promptText]);

  const handleOnClickGenerateButton = useCallback(async () => {
    if (!promptText) return;
    return await onClickActionButtonHandler(promptText);
  }, [onClickActionButtonHandler, promptText]);

  const [computedStyle, setComputedStyle] = useState(styles.actionButtonActive);
  const actionOnMouseEnterHandler = () => {
    setComputedStyle(styles.actionButtonActiveHovered);
  };
  const actionOnMouseLeaveHandler = () => {
    setComputedStyle(styles.actionButtonActive);
  };

  const sparklesContainerRef = useRef<HTMLDivElement>(null);
  const promptContainerRef = useRef<HTMLDivElement>(null);

  const resizeHandler = () => {
    if (!sparklesContainerRef.current || !promptContainerRef.current) return;
    const sparklesContainer = sparklesContainerRef.current;
    const promptContainer = promptContainerRef.current;

    const promptContainerRect = promptContainer.getBoundingClientRect();
    const sparklesContainerRect = sparklesContainer.getBoundingClientRect();

    const SPARKLES_TOP_OFFSET = 18;
    const sparklesTopOffset = promptContainerRect.top + SPARKLES_TOP_OFFSET;
    const sparklesLeftOffset = promptContainerRect.left + promptContainerRect.width - sparklesContainerRect.width;
    sparklesContainer.style.top = `${sparklesTopOffset}px`;
    sparklesContainer.style.left = `${sparklesLeftOffset}px`;
  };

  useLayoutEffect(() => {
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  return (
    <div style={styles.wrapper}>
      <div ref={promptContainerRef} id="prompt-container" style={{ ...styles.promptContainer, ...style }}>
        <div ref={sparklesContainerRef} id="illustrationContainer" style={styles.illustrationContainer}>
          <UIllustration illustration="sparkles" height={ILLUSTRATION_SIZE.height} width={ILLUSTRATION_SIZE.width} />
        </div>

        <div id="top-container" style={styles.topContainer}>
          <div style={styles.iaIcon}>
            <UIcon name="ia" size={IA_ICON_SIZE.size} color={COLORS.WHITE.DEFAULT} />
          </div>
          <h2 id="top-container_title" style={styles.title}>
            {strings.title}
          </h2>
          <div
            id="top-container_right-icon"
            className="UTag"
            style={styles.UTag}
            onClick={() => setShowDevMenu(!showdevMenu)}
          >
            {strings.tagBeta}
          </div>
        </div>

        <div id="middle-container" style={styles.middleContainer}>
          <SIAPrompt helpUrl={IA_HELP_URL} textValue={promptText} setTextValue={setPromptText} />
        </div>

        <div id="bottom-container" style={styles.bottomContainer}>
          <div onMouseEnter={actionOnMouseEnterHandler} onMouseLeave={actionOnMouseLeaveHandler}>
            <UButton
              leftIcon="sparkles"
              onClick={handleOnClickGenerateButton}
              text={strings.actionGenerate}
              textAndIconStyle={
                isGenerationButtonActive ? styles.actionButtonChildrenEnabled : styles.actionButtonChildrenDisabled
              }
              typography={'BODY3_SEMIBOLD'}
              disabled={!isGenerationButtonActive}
              style={isGenerationButtonActive ? computedStyle : {}}
            />
          </div>
          {displayGenerationCounter && (
            <UTextLink
              colorOverride={COLORS.DISABLED.DEFAULT}
              rightIcon="info-what"
              style={styles.footerCounterText}
              text={strings.generationCountdown}
              typography="BODY3_SEMIBOLD"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SIAPromptContainer;
